// Aesthetic: Operations console - slate ground, tabular counters, colour used
// only to separate "went to OneDrive" from "did not". The point of this dialog
// is not reassurance; it is an itemised account of what happened to each file.
import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import {
  Check,
  X,
  Loader2,
  AlertTriangle,
  Copy,
  RotateCcw,
  CircleDashed,
  MinusCircle
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter
} from './ui/dialog';
import { Button } from './ui/button';
import { Progress } from './ui/progress';
import { cn } from '../lib/utils';

const STATE = {
  PENDING: 'pending',
  RUNNING: 'running',
  DONE: 'done',
  FAILED: 'failed',
  SKIPPED: 'skipped'
};

const ROW_ICON = {
  [STATE.PENDING]: <CircleDashed className="h-4 w-4 text-slate-300 dark:text-slate-600" />,
  [STATE.RUNNING]: <Loader2 className="h-4 w-4 animate-spin text-blue-600 dark:text-blue-400" />,
  [STATE.DONE]: <Check className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />,
  [STATE.FAILED]: <X className="h-4 w-4 text-red-600 dark:text-red-400" />,
  [STATE.SKIPPED]: <MinusCircle className="h-4 w-4 text-slate-400 dark:text-slate-500" />
};

/**
 * Runs a list of jobs one at a time and shows exactly where it has got to.
 *
 * Sequential on purpose. Each job writes a file to OneDrive and, for a scanned
 * batch, cuts a page range out of a source file that later jobs still need.
 * Running them in parallel would be faster and would occasionally lose an
 * invoice, which is not a trade worth making.
 *
 * @param {object[]} jobs        [{ key, label, sublabel }]
 * @param {function} runJob      async (job) => ({ detail }) - throws to fail the job
 * @param {function} onFinished  called once with the summary when the run ends
 */
export function BulkProgressDialog({
  open,
  onOpenChange,
  title = 'Processing',
  description,
  jobs = [],
  runJob,
  onFinished,
  verb = 'Filed'
}) {
  const [rows, setRows] = useState([]);
  const [phase, setPhase] = useState('idle'); // idle | running | finished
  const stopped = useRef(false);
  const started = useRef(false);
  const runJobRef = useRef(runJob);
  const onFinishedRef = useRef(onFinished);

  useEffect(() => {
    runJobRef.current = runJob;
    onFinishedRef.current = onFinished;
  }, [runJob, onFinished]);

  const patch = useCallback((key, value) => {
    setRows((prev) => prev.map((row) => (row.key === key ? { ...row, ...value } : row)));
  }, []);

  const start = useCallback(
    async (queue) => {
      stopped.current = false;
      setPhase('running');
      setRows(queue.map((job) => ({ ...job, state: STATE.PENDING, detail: null, error: null })));

      const results = { done: 0, failed: 0, skipped: 0, failures: [] };

      for (const job of queue) {
        if (stopped.current) {
          patch(job.key, { state: STATE.SKIPPED });
          results.skipped += 1;
          continue;
        }

        patch(job.key, { state: STATE.RUNNING });
        try {
          const outcome = await runJobRef.current(job);
          patch(job.key, { state: STATE.DONE, detail: outcome?.detail || null });
          results.done += 1;
        } catch (err) {
          const message = err?.message || String(err);
          patch(job.key, { state: STATE.FAILED, error: message });
          results.failed += 1;
          results.failures.push({ ...job, error: message });
        }
      }

      setPhase('finished');
      onFinishedRef.current?.(results);
    },
    [patch]
  );

  // Kick off as soon as the dialog opens with work to do, and only once.
  useEffect(() => {
    if (open && jobs.length > 0 && !started.current) {
      started.current = true;
      start(jobs);
    }
    if (!open) {
      started.current = false;
      stopped.current = false;
      setPhase('idle');
      setRows([]);
    }
  }, [open, jobs, start]);

  const counts = useMemo(() => {
    const out = { done: 0, failed: 0, skipped: 0, pending: 0, running: 0 };
    for (const row of rows) {
      if (row.state === STATE.DONE) out.done += 1;
      else if (row.state === STATE.FAILED) out.failed += 1;
      else if (row.state === STATE.SKIPPED) out.skipped += 1;
      else if (row.state === STATE.RUNNING) out.running += 1;
      else out.pending += 1;
    }
    return out;
  }, [rows]);

  const settled = counts.done + counts.failed + counts.skipped;
  const percent = rows.length === 0 ? 0 : Math.round((settled / rows.length) * 100);
  const failures = rows.filter((row) => row.state === STATE.FAILED);
  const running = phase === 'running';

  const retryFailed = () => {
    const retryJobs = failures.map(({ key, label, sublabel, payload }) => ({
      key,
      label,
      sublabel,
      payload
    }));
    if (retryJobs.length > 0) start(retryJobs);
  };

  const copyFailures = async () => {
    const text = failures.map((row) => `${row.label}\t${row.error}`).join('\n');
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      // Clipboard access can be denied; the list is on screen either way.
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        // Closing mid-run would hide the only record of what happened.
        if (!next && running) return;
        onOpenChange?.(next);
      }}
    >
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {running && <Loader2 className="h-4 w-4 animate-spin text-blue-600" />}
            {title}
          </DialogTitle>
          {description && <DialogDescription>{description}</DialogDescription>}
        </DialogHeader>

        <div className="space-y-2">
          <div className="flex items-baseline justify-between text-sm">
            <span className="font-mono tabular-nums text-slate-700 dark:text-slate-300">
              {settled}/{rows.length} processed
            </span>
            <span className="flex items-center gap-3 text-xs tabular-nums">
              <span className="text-emerald-700 dark:text-emerald-400">{counts.done} {verb.toLowerCase()}</span>
              {counts.failed > 0 && (
                <span className="font-semibold text-red-700 dark:text-red-400">
                  {counts.failed} failed
                </span>
              )}
              {counts.skipped > 0 && (
                <span className="text-slate-500 dark:text-slate-400">{counts.skipped} skipped</span>
              )}
            </span>
          </div>
          <Progress value={percent} className="h-2" />
        </div>

        <div className="max-h-[45vh] space-y-1 overflow-y-auto rounded-md border border-slate-200 p-2 dark:border-slate-800">
          <AnimatePresence initial={false}>
            {rows.map((row) => (
              <motion.div
                key={row.key}
                layout
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.15 }}
                className={cn(
                  'flex items-start gap-2.5 rounded px-2 py-1.5 text-sm',
                  row.state === STATE.RUNNING && 'bg-blue-50 dark:bg-blue-950/40',
                  row.state === STATE.FAILED && 'bg-red-50 dark:bg-red-950/30'
                )}
              >
                <span className="mt-0.5 shrink-0">{ROW_ICON[row.state]}</span>
                <div className="min-w-0 flex-1">
                  <div className="truncate font-medium text-slate-800 dark:text-slate-200">
                    {row.label}
                  </div>
                  {row.state === STATE.DONE && row.detail && (
                    <div className="truncate font-mono text-xs text-emerald-700 dark:text-emerald-400">
                      {row.detail}
                    </div>
                  )}
                  {row.state === STATE.FAILED && (
                    <div className="text-xs text-red-700 dark:text-red-400">{row.error}</div>
                  )}
                  {row.state !== STATE.DONE && row.state !== STATE.FAILED && row.sublabel && (
                    <div className="truncate text-xs text-slate-500 dark:text-slate-400">
                      {row.sublabel}
                    </div>
                  )}
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
        </div>

        {phase === 'finished' && counts.failed > 0 && (
          <div className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm dark:border-amber-800 dark:bg-amber-950/40">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600 dark:text-amber-400" />
            <span className="text-amber-900 dark:text-amber-200">
              {counts.failed} file{counts.failed > 1 ? 's were' : ' was'} not written to OneDrive and{' '}
              {counts.failed > 1 ? 'remain' : 'remains'} in the review queue. Follow{' '}
              {counts.failed > 1 ? 'them' : 'it'} up before the batch is considered done.
            </span>
          </div>
        )}

        <DialogFooter className="gap-2 sm:justify-between">
          <div className="flex gap-2">
            {phase === 'finished' && failures.length > 0 && (
              <>
                <Button type="button" variant="outline" size="sm" onClick={copyFailures}>
                  <Copy className="mr-1.5 h-3.5 w-3.5" />
                  Copy failures
                </Button>
                <Button type="button" variant="outline" size="sm" onClick={retryFailed}>
                  <RotateCcw className="mr-1.5 h-3.5 w-3.5" />
                  Retry {failures.length}
                </Button>
              </>
            )}
          </div>

          {running ? (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => {
                stopped.current = true;
              }}
            >
              Stop after this one
            </Button>
          ) : (
            <Button type="button" size="sm" onClick={() => onOpenChange?.(false)}>
              Done
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default BulkProgressDialog;
